import { Link, NavLink } from 'react-router-dom';
import {
  BarChart3,
  BookOpen,
  FileCheck,
  Sparkles,
  Cpu,
  FolderGit2,
  Handshake,
  Award,
  ShieldCheck,
  Users,
  Building2,
  GraduationCap,
  Tag,
  TicketPercent,
  CreditCard,
  Bell,
  ChevronDown,
  BrainCircuit,
} from 'lucide-react';
import { cn } from '@/utils/styleUtils';
import { useState } from 'react';
import { useAppSelector } from '@/store/hooks';
import { selectCurrentAdmin } from '@/store/selectors';
import { UserRole } from '@/types/auth';
import { routePermissions } from '@/routes/config';

interface IProps {
  isSideNavOpen: boolean;
}

const SideNav = ({ isSideNavOpen }: IProps) => {
  const userData = useAppSelector(selectCurrentAdmin);
  const [expandedGroups, setExpandedGroups] = useState<{ [key: string]: boolean }>({
    courses: true,
    community: true,
    sales: true,
  });

  const toggleGroup = (groupId: string) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupId]: !prev[groupId],
    }));
  };

  const menuItems = [
    {
      title: 'Statistika',
      icon: BarChart3,
      link: '/',
    },
    {
      groupId: 'courses',
      title: 'Kurslar Boshqaruvi',
      icon: BookOpen,
      items: [
        {
          title: 'Darslar',
          icon: BookOpen,
          link: '/lessons',
        },
        { title: 'Yakuniy Imtihonlar', link: '/exams', icon: FileCheck },
        { title: 'Ko\'nikmalar', link: '/course-skills', icon: Sparkles },
        { title: 'Texnologiyalar', link: '/course-technologies', icon: Cpu },
        { title: 'Loyihalar', link: '/course-projects', icon: FolderGit2 },
        { title: 'Kurs Hamkorlari', link: '/course-partners', icon: Handshake },
        { title: 'Nishonlar (Badges)', link: '/badges', icon: Award },
        { title: 'Sertifikatlar', link: '/certificates', icon: ShieldCheck },
      ],
    },
    {
      groupId: 'community',
      title: 'Ustoz va Hamkorlar',
      icon: Users,
      items: [
        { title: 'Mentorlar', link: '/mentors', icon: Users },
        { title: 'Hamkorlar', link: '/partners', icon: Building2 },
        { title: 'Bitiruvchilar', link: '/graduates', icon: GraduationCap },
      ],
    },
    {
      groupId: 'sales',
      title: 'Savdo va Promokodlar',
      icon: Tag,
      items: [
        { title: 'Presales (Oldindan sotuv)', link: '/presales', icon: Tag },
        { title: 'Promokodlar', link: '/promocodes', icon: TicketPercent },
        { title: 'Obuna Rejalari', link: '/subscription-plans', icon: CreditCard },
      ],
    },
    {
      title: 'Bildirishnomalar',
      icon: Bell,
      link: '/notifications',
    },
  ];

  const hasPermission = (item: any): boolean => {
    if (!userData?.role) return true;
    if (userData.role === UserRole.SUPER_ADMIN) return true;

    if (item.link) {
      return routePermissions[item.link]?.includes(userData.role) || false;
    }

    if (item.items) {
      return item.items.some((subItem: any) => {
        if (subItem.link) return routePermissions[subItem.link]?.includes(userData.role);
        if (subItem.subItems) return subItem.subItems.some((s: any) => routePermissions[s.link]?.includes(userData.role));
        return false;
      });
    }

    return false;
  };

  const filteredMenuItems = menuItems.filter(hasPermission);

  return (
    <aside
      className={cn(
        'sticky top-0 h-screen flex flex-col bg-card text-card-foreground border-r border-border transition-all duration-300 z-40 overflow-hidden',
        isSideNavOpen ? 'w-[270px] min-w-[270px]' : 'w-[80px] min-w-[80px]'
      )}
    >
      <header className="flex items-center h-[72px] px-6 border-b border-border shrink-0">
        <Link to="/" className={`flex items-center gap-3 text-foreground w-full ${isSideNavOpen ? 'justify-start' : 'justify-center'}`}>
          <div className="flex items-center justify-center bg-primary rounded-xl text-white p-2 shadow-sm shrink-0">
            <BrainCircuit className="size-5" />
          </div>
          {isSideNavOpen && <span className="font-bold text-xl tracking-tight text-foreground whitespace-nowrap">01AI Admin</span>}
        </Link>
      </header>

      <div className="flex-1 overflow-y-auto py-4 px-3.5 flex flex-col gap-1 custom-scrollbar">
        {filteredMenuItems.map((item, index) => (
          <div key={index} className="mb-1">
            {item.items ? (
              <div className="flex flex-col gap-0.5">
                <button
                  onClick={() => toggleGroup(item.groupId)}
                  className={cn(
                    'w-full flex items-center rounded-xl px-3 py-2.5 font-medium text-sm transition-all duration-200',
                    isSideNavOpen ? 'justify-between' : 'justify-center',
                    expandedGroups[item.groupId]
                      ? 'text-primary bg-primary/10 font-semibold'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  )}
                >
                  <div className="flex items-center gap-3">
                    <item.icon className={cn('size-4.5 shrink-0', expandedGroups[item.groupId] ? 'text-primary' : 'text-muted-foreground')} />
                    {isSideNavOpen && <span className="whitespace-nowrap">{item.title}</span>}
                  </div>
                  {isSideNavOpen && (
                    <ChevronDown
                      className={cn(
                        'size-4 shrink-0 transition-transform duration-200',
                        expandedGroups[item.groupId] ? 'rotate-180 text-primary' : 'text-muted-foreground'
                      )}
                    />
                  )}
                </button>

                {expandedGroups[item.groupId] && isSideNavOpen && (
                  <div className="flex flex-col gap-0.5 mt-0.5 pl-3 border-l-2 border-border ml-5">
                    {item.items.map((subItem: any, subIndex: number) => (
                      <NavLink
                        to={subItem.link}
                        key={subIndex}
                        className={({ isActive }) =>
                          cn(
                            'flex items-center px-3 py-2 rounded-lg text-[13px] font-medium transition-all duration-200',
                            isActive
                              ? 'bg-primary text-white shadow-sm font-semibold'
                              : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                          )
                        }
                      >
                        <span className="truncate">{subItem.title}</span>
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <NavLink
                to={item.link}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-200',
                    isSideNavOpen ? 'justify-start' : 'justify-center',
                    isActive
                      ? 'bg-primary text-white shadow-sm font-semibold'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground group'
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    <item.icon
                      className={cn(
                        'size-4.5 shrink-0',
                        isActive ? 'text-white' : 'text-muted-foreground group-hover:text-foreground'
                      )}
                    />
                    {isSideNavOpen && <span className="whitespace-nowrap truncate">{item.title}</span>}
                  </>
                )}
              </NavLink>
            )}
          </div>
        ))}
      </div>
    </aside>
  );
};

export default SideNav;
